import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, Trash2, UserPlus } from "lucide-react";
import api from "../lib/api";
import { formatLocalDate } from "../lib/datetime";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "./ui/sheet";

const inputStyle = { background: "var(--input-bg)", border: "1px solid var(--input-border)", color: "var(--fg)" };

/** Superadmin drawer: list / add / remove members of one organization. */
export default function AdminOrgMembersSheet({ org, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [removing, setRemoving] = useState("");

  const load = useCallback(() => {
    if (!org) return;
    api.get(`/admin/orgs/${org.id}/members`)
      .then(({ data: d }) => setData(d))
      .catch((err) => setError(err?.response?.data?.detail || "Failed to load members"));
  }, [org]);

  useEffect(() => { setData(null); setError(""); setEmail(""); setRole("member"); load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true); setError("");
    try {
      const { data: d } = await api.post(`/admin/orgs/${org.id}/members`, { email: email.trim(), role });
      toast.success(d.already_member ? (d.role_changed ? `Role updated to ${d.role}` : "Already a member") : `Added ${d.email}`);
      setEmail("");
      load(); onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't add member");
    } finally { setBusy(false); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email || m.user_id} from ${data?.name || "this org"}?`)) return;
    setRemoving(m.user_id); setError("");
    try {
      await api.delete(`/admin/orgs/${org.id}/members/${m.user_id}`);
      toast.success("Member removed");
      load(); onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't remove member");
    } finally { setRemoving(""); }
  };

  return (
    <Sheet open={!!org} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="admin-org-members-sheet">
        <SheetHeader>
          <SheetTitle data-testid="admin-org-members-title">{data?.name || org?.name || "Organization"} — members</SheetTitle>
          <SheetDescription>Add or remove people directly. They must already have an account.</SheetDescription>
        </SheetHeader>

        <form onSubmit={add} className="mt-5 flex flex-wrap gap-2" data-testid="admin-org-add-member-form">
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="person@company.com" className="flex-1 min-w-[180px] rounded-xl px-3 py-2 text-sm"
            style={inputStyle} data-testid="admin-org-add-member-email"
          />
          <select
            value={role} onChange={(e) => setRole(e.target.value)}
            className="rounded-xl px-3 py-2 text-sm" style={inputStyle} data-testid="admin-org-add-member-role"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" disabled={busy || !email.trim()} className="sp-btn sp-btn-primary text-xs disabled:opacity-50" data-testid="admin-org-add-member-submit">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />} Add member
          </button>
        </form>
        {error && <p className="text-xs text-red-400 mt-2" data-testid="admin-org-members-error">{error}</p>}

        <div className="mt-5 space-y-2" data-testid="admin-org-members-list">
          {!data ? (
            <div className="py-6 text-center sp-subtle"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
          ) : data.rows.length === 0 ? (
            <p className="text-xs sp-subtle italic">No members.</p>
          ) : data.rows.map((m) => (
            <div key={m.user_id} className="rounded-xl px-3 py-2.5 flex items-center gap-3 text-sm" style={{ border: "1px solid var(--border-soft)" }} data-testid={`admin-org-member-row-${m.user_id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">{m.name || m.email || m.user_id}</div>
                <div className="text-xs sp-subtle truncate">{m.email || "(account missing)"} · joined {m.joined_at ? formatLocalDate(m.joined_at) : "—"}</div>
              </div>
              {m.is_owner ? (
                <span className="sp-chip text-[10px]" data-testid={`admin-org-member-owner-${m.user_id}`}><Crown className="w-3 h-3" /> Owner</span>
              ) : (
                <>
                  <span className="sp-chip text-[10px] capitalize" data-testid={`admin-org-member-role-${m.user_id}`}>{m.role}</span>
                  <button type="button" onClick={() => remove(m)} disabled={removing === m.user_id}
                    className="sp-btn sp-btn-ghost text-xs hover:text-red-400 disabled:opacity-50"
                    data-testid={`admin-org-member-remove-${m.user_id}`} title="Remove from org">
                    {removing === m.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
